import type { Metadata } from "next"
import { notFound } from "next/navigation"
import { Suspense } from "react"
import { RelatedProducts } from "@/components/e-commerce/product-details/related-products"
import { RelatedProductsSkeleton } from "@/components/e-commerce/product-details/related-products/skeleton"
import { ProductDetailsSlider } from "@/components/e-commerce/product-details/slider"
import { ProductDetailsSummary } from "@/components/e-commerce/product-details/summary"
import { ProductDetailsTabs } from "@/components/e-commerce/product-details/tabs"
import { env } from "@/env"
import {
  getProductBySlug,
  type WooCommerceProduct,
} from "@/services/woo-commerce-service"

interface PageParams {
  slug: string
}

const HTML_TAG_REGEX = /<[^>]*>/g
const DESCRIPTION_MAX_LENGTH = 160

function extractProductDescription(product: WooCommerceProduct): string {
  const rawDescription = product.description
    ?.replace(HTML_TAG_REGEX, "")
    .slice(0, DESCRIPTION_MAX_LENGTH)
  return rawDescription ?? product.short_description ?? ""
}

export async function generateMetadata({
  params,
}: {
  params: Promise<{ slug: string }>
}): Promise<Metadata> {
  const { slug } = await params
  const product = await getProductBySlug(slug)

  if (!product) {
    return { title: "Produto não encontrado" }
  }

  const siteUrl = env.NEXT_PUBLIC_SITE_URL ?? "http://localhost:3000"
  const productImage = product.images?.[0]?.src ?? ""
  const productDescription = extractProductDescription(product)
  const canonicalUrl = `${siteUrl}/produtos/${slug}`

  return {
    metadataBase: new URL(siteUrl),
    title: product.name,
    description: productDescription,
    keywords: [product.name, product.sku],

    robots: {
      index: true,
      follow: true,
      googleBot: "index, follow",
    },

    openGraph: {
      title: product.name,
      description: productDescription,
      type: "website",
      url: canonicalUrl,
      images: productImage ? [{ url: productImage, alt: product.name }] : [],
    },

    twitter: {
      card: "summary_large_image",
      title: product.name,
      description: productDescription,
      images: productImage ? [productImage] : [],
    },

    alternates: {
      canonical: canonicalUrl,
    },
  }
}

export default async function Page({
  params,
  searchParams,
}: {
  params: Promise<PageParams>
  searchParams: Promise<{ tab?: string; writeReview?: string }>
}) {
  const { slug } = await params
  const { tab, writeReview } = await searchParams

  const product = await getProductBySlug(slug)

  if (!product) {
    return notFound()
  }

  return (
    <div className='mx-auto flex w-full max-w-400 flex-col gap-4 px-7 text-gray-900'>
      <h1
        className='line-clamp-2 text-[clamp(1.5rem,2vw,3rem)]'
        title={product.name}
      >
        {product.name}
      </h1>

      <span className='font-mono font-semibold text-gray-500 text-sm'>
        SKU: {product.sku || "—"}
      </span>

      <hr className='border-gray-200' />

      <div className='flex flex-wrap items-start justify-center gap-3'>
        <ProductDetailsSlider
          className='min-w-0 max-w-150 flex-1 basis-125'
          product={product}
        />

        <ProductDetailsSummary
          className='min-w-0 flex-1 basis-125'
          product={product}
        />
      </div>

      <ProductDetailsTabs
        initialTab={tab === "reviews" ? "reviews" : "description"}
        product={product}
        writeReview={writeReview === "1"}
      />

      <Suspense fallback={<RelatedProductsSkeleton />}>
        <RelatedProducts product={product} />
      </Suspense>
    </div>
  )
}
